import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Mail, Phone, MapPin, CheckCircle2, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API_URL, APP_URL } from "@/lib/links";
import { FloatingPatterns } from "@/components/home/FloatingPatterns";

const info = [
  { icon: Building2, label: "Company", value: "Xpattern", id: "contact-info-company" },
  { icon: Mail, label: "Email", value: "support@xpattern.net", href: "mailto:support@xpattern.net", id: "contact-info-email" },
  { icon: Phone, label: "Phone", value: "503.328.4130", href: "tel:+15033284130", id: "contact-info-phone" },
  { icon: MapPin, label: "Location", value: "Portland, Oregon", id: "contact-info-location" },
];

const fields = [
  { name: "name", label: "Your name", type: "text", required: true, autoComplete: "name" },
  { name: "company", label: "Shop/company", type: "text", autoComplete: "organization" },
  { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
  { name: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
];

const empty = { name: "", company: "", email: "", phone: "", message: "" };

const ContactForm = () => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API_URL}/contact`, form);
      fetch(`${APP_URL}/api/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.name, email: form.email, message: form.message }) }).catch(() => {});
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="card p-7 sm:p-9" data-testid="contact-form-card">
      <AnimatePresence mode="wait">
        {status === "sent" ? (
          <motion.div
            key="sent"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex min-h-[420px] flex-col items-center justify-center text-center"
            data-testid="contact-success-state"
          >
            <motion.span
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40"
            >
              <CheckCircle2 size={34} />
            </motion.span>
            <h3 className="display mt-6 text-3xl text-white">Message sent</h3>
            <p className="mt-3 max-w-sm text-muted">Thanks, {form.name.split(" ")[0]}. We'll get back to you at {form.email} shortly.</p>
            <button
              type="button"
              className="btn btn-secondary mt-8"
              onClick={() => { setForm(empty); setStatus("idle"); }}
              data-testid="contact-send-another-button"
            >
              Send another message
            </button>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={onSubmit} className="space-y-5" data-testid="contact-form" exit={{ opacity: 0 }}>
            <div className="grid gap-5 sm:grid-cols-2">
              {fields.map((f) => (
                <label key={f.name} className="block">
                  <span className="mb-2 block text-sm font-medium text-silver">{f.label}</span>
                  <input
                    className="field"
                    name={f.name}
                    type={f.type}
                    required={f.required}
                    autoComplete={f.autoComplete}
                    value={form[f.name]}
                    onChange={onChange}
                    data-testid={`contact-input-${f.name}`}
                  />
                </label>
              ))}
            </div>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-silver">How can we help?</span>
              <textarea
                className="field min-h-[140px] resize-y"
                name="message"
                required
                value={form.message}
                onChange={onChange}
                data-testid="contact-input-message"
              />
            </label>
            {error && (
              <p className="text-sm text-red-400" data-testid="contact-error">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary w-full sm:w-auto" disabled={status === "loading"} data-testid="contact-submit-button">
              {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {status === "loading" ? "Sending…" : "Send message"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Contact() {
  return (
    <div className="relative overflow-hidden" data-testid="contact-page">
      <div className="starfield opacity-60" aria-hidden />
      <div aria-hidden className="grid-lines pointer-events-none absolute inset-x-0 top-0 h-[520px] opacity-60" />
      <FloatingPatterns count={2} />
      <div aria-hidden className="pointer-events-none absolute -left-40 top-0 h-[480px] w-[620px] rounded-full bg-blue/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-16 sm:px-8 sm:pt-20 lg:grid-cols-12 lg:gap-16 lg:pb-32">
        <Reveal className="lg:col-span-5">
          <p className="mono-label">Xpattern // Contact</p>
          <h1 className="display mt-5 text-5xl text-white sm:text-6xl lg:text-8xl" data-testid="contact-headline">
            Talk to a <span className="text-cyan-glow">real person.</span>
          </h1>
          <p className="mt-7 max-w-md text-base leading-relaxed text-muted sm:text-lg" data-testid="contact-subcopy">
            Questions about a plotter, a vehicle we should add, your subscription, or pricing for multiple shops? We answer fast.
          </p>
          <ul className="mt-12 space-y-5" data-testid="contact-info-list">
            {info.map((it) => (
              <li key={it.label} className="flex items-center gap-4" data-testid={it.id}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue/15 text-blue-text ring-1 ring-blue/30">
                  <it.icon size={19} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted">{it.label}</p>
                  {it.href ? (
                    <a href={it.href} className="text-base text-body transition-colors hover:text-blue-text">{it.value}</a>
                  ) : (
                    <p className="text-base text-body">{it.value}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <ContactForm />
        </Reveal>
      </div>

    </div>
  );
}
